import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import {
  Wrench,
  CheckCircle2,
  XCircle,
  ArrowLeft,
  Filter,
  RefreshCw,
  Eye,
  Copy,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { SyncedTopScrollbar } from "@/components/ui/synced-top-scrollbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Spinner } from "@/components/ui/spinner";
import { SearchInput } from "@/components/ui/search-input";
import { Pagination } from "@/components/ui/pagination";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { NeedsMaintenanceCell } from "@/components/portal/platform/NeedsMaintenanceCell";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { describeLinkFkUpgradeResult } from "@/lib/myapp/link-fk-upgrade-status";
import { useSortableData } from "@/hooks/useSortableData";
import type { SortableColumn } from "@/hooks/useSortableData";
import { useClientPaginatedData } from "@/hooks/usePaginatedData";
import { myappApi } from "@/services/api/myapp";
import { useRepairStatusPolling } from "@/hooks/useRepairStatusPolling";
import { useMaintenanceRunPolling } from "@/hooks/useMaintenanceRunPolling";
import type { RepairResult } from "@/hooks/useMaintenanceRunPolling";
import { useAuth } from "@/contexts/AuthContext";
import type { Form } from "@/types/myapp";

export const Route = createFileRoute("/portal/platform/maintenance")({
  component: MaintenancePage,
});

// After a Run Maintenance, re-scan only the maintained tables when there are
// at most this many (keeps the synchronous re-scan well inside the gateway
// budget); above it, fall back to a full org scan.
const RESCAN_THRESHOLD = 5;

// Manual "Scan selected" is capped to this many tables: the synchronous rescan
// endpoint scans serially in-request, so a small batch stays well within the
// gateway budget and never lights up the worker the way a full org scan does.
const SCAN_SELECTED_MAX = 5;

// Edge shadows that mark the sticky (pinned) columns so content reads as
// scrolling UNDER them — left edge for the pinned name group, right for actions.
const STICKY_LEFT_SHADOW = "shadow-[4px_0_6px_-4px_rgba(0,0,0,0.12)]";
const STICKY_RIGHT_SHADOW = "shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.12)]";

// BE UTC stamps are serialised offset-less (naive DATETIME), so `new Date(s)`
// would read them as LOCAL time and shift the instant by the browser's UTC
// offset. Anchor the parse as UTC unless the string already carries a zone,
// and return null (never throw) on anything unparseable so a malformed stamp
// can't break the page render.
function parseUtcStamp(value: string | null | undefined): Date | null {
  if (!value) return null;
  // Treat as already-zoned if it ends in Z or a ±HH, ±HHMM or ±HH:MM offset.
  const anchored = /[zZ]$|[+-]\d{2}(:?\d{2})?$/.test(value)
    ? value
    : `${value}Z`;
  const parsed = new Date(anchored);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

// A maintenance table row augmented with the sortable/precomputed keys the
// grid injects: the session "last scanned" stamp, the UTC-anchored "last
// repaired" Date (parsed once, shared by sort and render), and the status
// rank used for the Status column's sort.
type MaintenanceRow = Form & {
  last_scanned_at: string | null;
  last_repaired_at: Date | null;
  status_rank: number;
};

// Stable sort-column config (defined once, not per render) so useSortableData's
// memoised comparator map and sorted output don't recompute every render. The
// Last Repaired column sorts on the pre-parsed Date's epoch so its order
// matches the rendered cell exactly rather than a lexicographic sort of the
// raw stamp string.
const MAINTENANCE_SORT_COLUMNS: SortableColumn<Record<string, unknown>>[] = [
  {
    key: "last_repair_run_at_utc",
    getValue: (row) => (row.last_repaired_at as Date | null)?.getTime() ?? null,
  },
];

// Build the copyable plain-text table for a result's unresolved link rows.
// Tab-separated so it pastes cleanly into a spreadsheet or ticket. The header
// line names the table and true total (the row list is capped server-side).
function buildUnresolvedText(r: RepairResult): string {
  const header = [
    "Header table",
    "Grain",
    "Record",
    "Parent record",
    "Field",
    "Stored value",
    "Reason",
  ].join("\t");
  const lines = r.unresolvedRows.map((row) =>
    [
      row.header_table,
      row.grain,
      row.record_identifier ?? "",
      row.parent_record ?? "",
      row.field_name,
      row.stored_value ?? "",
      row.reason,
    ].join("\t"),
  );
  const capped =
    r.unresolvedTotal > r.unresolvedRows.length
      ? `\n(showing ${r.unresolvedRows.length} of ${r.unresolvedTotal} unresolved rows)`
      : "";
  return [header, ...lines].join("\n") + capped;
}

function MaintenancePage() {
  const { token } = useAuth();
  const [tables, setTables] = useState<Form[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Pre-emptive repair status: indexed by table_id so the "Needs
  // Maintenance" column can look up status by row in O(1). The worker
  // scan streams rows in incrementally (scanning=true + progress); a
  // missing key while scanning means "not scanned yet" (skeleton), a
  // missing key after completion means no issues. statusFetchFailed
  // drives the stale-column banner — the FE and BE both decline to
  // silently swallow a status-check error.
  const {
    statusByTableId,
    statusLoading,
    scanning,
    progress: scanProgress,
    statusFetchFailed,
    refreshStatus,
    applyStatuses,
    lastScannedByTableId,
  } = useRepairStatusPolling(token, { autoStart: false });

  // Manual scan of the SELECTED tables (synchronous, bounded). Opening the
  // screen no longer triggers the org-wide worker sweep — that scan over every
  // table is what overloaded the worker on 2026-06-29. The admin now scans in
  // small batches on demand. Spinner state for the "Scan selected" button.
  const [isScanningSelected, setIsScanningSelected] = useState(false);
  // The row currently running a per-row "Scan" (drives that row's spinner).
  const [scanningRowId, setScanningRowId] = useState<string | null>(null);

  // Horizontal scroll sync: the table lives in a bounded scroll container
  // (sticky header + sticky first/last columns); a proxy scrollbar on top mirrors
  // its horizontal scroll so a wide table can be dragged from either end and
  // never bleeds off the page. Mirrors the reporting datasource pattern.
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const [tableContentWidth, setTableContentWidth] = useState(0);

  // Multi-select
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Bulk repair runs on the myapp worker (off the 120s-capped gateway
  // request path); this hook enqueues the run and polls it, streaming
  // per-table results in as each table completes.
  const {
    runResults: results,
    runProgress,
    isRunning,
    runFailed,
    startRun,
    dismissRunFailure,
  } = useMaintenanceRunPolling(token);
  const [showResults, setShowResults] = useState(false);

  // "Needs maintenance only" filter + "select all with issues". A table needs
  // maintenance when its scanned status carries issues, or it needs a
  // republish. (While scanning, a not-yet-scanned table reads as clean — the
  // counts settle as rows stream in.)
  const [showOnlyNeedsMaintenance, setShowOnlyNeedsMaintenance] =
    useState(false);

  const tableNeedsMaintenance = useCallback(
    (t: Form) => {
      const s = statusByTableId[t.id];
      const hasIssues = !!s && (s.needs_repair || (s.issues?.length ?? 0) > 0);
      return hasIssues || (t.needs_republish ?? false);
    },
    [statusByTableId],
  );

  // A sortable severity rank for the Status column (which renders a component,
  // not a plain field). Higher = more urgent, so a desc sort surfaces criticals
  // first; "not scanned" sorts to the bottom as a distinct unknown bucket.
  const statusRank = useCallback(
    (t: Form) => {
      const s = statusByTableId[t.id];
      if (!s && !(t.needs_republish ?? false)) return -1; // unknown / not scanned
      const critical =
        s?.issues
          ?.filter((i) => i.severity === "critical")
          .reduce((n, i) => n + i.count, 0) ?? 0;
      const warning =
        s?.issues
          ?.filter((i) => i.severity === "warning")
          .reduce((n, i) => n + i.count, 0) ?? 0;
      if (critical > 0) return 3000 + critical;
      if (t.needs_republish ?? false) return 2000;
      if (warning > 0) return 1000 + warning;
      return 0; // scanned, clean
    },
    [statusByTableId],
  );

  const needingCount = tables.filter(tableNeedsMaintenance).length;

  // The per-field FK upgrade is only RELEVANT to a table that still has a link
  // field missing its canonical SysFk column — the BE flags that as a
  // MISSING_FK_COLUMNS issue in the repair-status scan (rolled up to the parent
  // row even when the collision lives on a hidden Lines/Versions child). Tables
  // with no link fields, or already fully on the per-field scheme, have no such
  // issue → the Upgrade button is hidden (nothing to upgrade). Mirrors the
  // authoritative signal the rest of the screen already runs, so we don't add a
  // second source of truth.
  const needsLinkFkUpgrade = useCallback(
    (tableId: string) =>
      !!statusByTableId[tableId]?.issues?.some(
        (i) => i.code === "MISSING_FK_COLUMNS",
      ),
    [statusByTableId],
  );

  // Search is CLIENT-SIDE: we already load every table (fetch-all below), so we
  // filter the loaded set instead of re-fetching the whole org per keystroke
  // (the old behaviour flickered the list to a full-page spinner on every key).
  const search = searchTerm.trim().toLowerCase();
  const filteredTables = useMemo(
    () =>
      (showOnlyNeedsMaintenance
        ? tables.filter(tableNeedsMaintenance)
        : tables
      ).filter(
        (t) =>
          !search ||
          t.name.toLowerCase().includes(search) ||
          (t.table_name ?? "").toLowerCase().includes(search),
      ),
    [tables, showOnlyNeedsMaintenance, search, tableNeedsMaintenance],
  );

  // Inject the session "last scanned" timestamp so the column can be sorted
  // (useSortableData sorts by a key on the row). Rendering reads the map
  // directly; this is purely to make the column sortable. Also parse the
  // "last repaired" stamp ONCE here (UTC-anchored) so sort and render share
  // the exact same instant — sorting on the raw string would use the hook's
  // local-time Date.parse and could contradict the UTC-anchored cell for any
  // offset-less/zoned mix.
  const tablesWithScan = useMemo(
    () =>
      filteredTables.map((t) => ({
        ...t,
        last_scanned_at: lastScannedByTableId[t.id] ?? null,
        last_repaired_at: parseUtcStamp(t.last_repair_run_at_utc),
        status_rank: statusRank(t),
      })),
    [filteredTables, lastScannedByTableId, statusRank],
  );

  const selectAllNeedingMaintenance = () => {
    setSelectedIds(
      new Set(tables.filter(tableNeedsMaintenance).map((t) => t.id)),
    );
  };

  // Sort tables (over the filtered set). The Last Repaired column sorts on the
  // pre-parsed Date's epoch so its order matches the rendered cell exactly
  // rather than lexicographically on the raw stamp string. Missing stamps sort
  // last ascending / first descending (the shared hook's null handling).
  const { sortedData, requestSort, getSortDirection } = useSortableData(
    tablesWithScan as unknown as Record<string, unknown>[],
    {
      initialSort: { key: "name", direction: "asc" },
      columns: MAINTENANCE_SORT_COLUMNS,
    },
  );

  // Client-side pagination on sorted data
  const {
    paginatedData,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
  } = useClientPaginatedData<Record<string, unknown>>({
    data: sortedData,
    itemsPerPage: 25,
  });

  const sortedTables = paginatedData as unknown as MaintenanceRow[];

  const fetchTables = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      // Maintenance must see EVERY table, not just the first page. The BE
      // caps `limit` at 100 (shared_utils pagination le=100), so a single
      // unpaged call silently returned only the first 25 — the screen then
      // paginated those 25 and the rest of the org's tables (and their
      // collision/repair state) were invisible. Page through until we've
      // pulled `total` (or hit a short final page), then paginate/sort/filter
      // the full set client-side.
      const PAGE_SIZE = 100;
      // Hard cap so a misbehaving BE (full pages + inflated `total`) can never
      // spin forever — 200 pages = 20k tables, far beyond any real org.
      const MAX_PAGES = 200;
      const all: Form[] = [];
      let offset = 0;
      for (let i = 0; i < MAX_PAGES; i++) {
        const response = await myappApi.tables.list(token, {
          // No server-side `search` here: we load the FULL set once and filter
          // client-side (see filteredTables), so typing never re-fetches.
          isPublished: true,
          showHidden: true,
          sortBy: "name",
          limit: PAGE_SIZE,
          offset,
        });
        all.push(...response.items);
        if (
          response.items.length < PAGE_SIZE ||
          all.length >= response.total
        ) {
          break;
        }
        offset += PAGE_SIZE;
      }
      setTables(all);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch tables");
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Per-field SysFk upgrade ("Upgrade Link Table Fields"), run per-row from the
  // maintenance screen. upgradeTarget drives the confirm dialog; upgradingId is
  // the row currently applying (disables its button + shows a spinner).
  const [upgradeTarget, setUpgradeTarget] = useState<Form | null>(null);
  const [upgradingId, setUpgradingId] = useState<string | null>(null);

  const handleConfirmUpgrade = useCallback(async () => {
    if (!token || !upgradeTarget) return;
    const target = upgradeTarget;
    setUpgradeTarget(null);
    setUpgradingId(target.id);
    try {
      const result = await myappApi.fields.upgradeLinkFk(token, target.id, false);
      const summary = describeLinkFkUpgradeResult(
        result.total_added,
        result.errors,
        result.total_populated,
        result.total_unresolved,
      );
      if (result.errors && result.errors.length > 0) toast.warning(summary);
      else toast.success(summary);
      await fetchTables(); // refresh so the row flips to "upgraded {date}"
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Upgrade failed",
      );
    } finally {
      setUpgradingId(null);
    }
  }, [token, upgradeTarget, fetchTables]);

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  // Client-side search/filter can shrink the list — snap back to page 1 so we're
  // never stranded on an empty trailing page.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, showOnlyNeedsMaintenance, setCurrentPage]);

  // Keep the top proxy scrollbar's width in sync with the table's scroll width
  // (re-measures on data/size changes via ResizeObserver).
  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const update = () => setTableContentWidth(el.scrollWidth);
    update();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [filteredTables.length, loading]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    // Select/deselect all VISIBLE tables (the filtered set, not just the
    // current page). With no filter active this is every table, as before.
    const visibleIds = filteredTables.map((t) => t.id);
    const allVisibleSelected =
      visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  // Run a worker maintenance over `ids` (bulk selection or a single row). The
  // run streams results back; onComplete refreshes the list + re-scans the
  // maintained tables (targeted for a small set, else a full sweep).
  const runMaintenanceFor = async (ids: string[]) => {
    if (ids.length === 0 || !token) return;
    setShowResults(true);
    await startRun(ids, {
      onComplete: async (finalResults) => {
        await fetchTables(); // LastPublishedAtUtc / badge
        if (token && ids.length <= RESCAN_THRESHOLD) {
          try {
            const res = await myappApi.tables.rescanStatus(token, ids);
            applyStatuses(res.tables);
          } catch {
            refreshStatus(); // targeted re-scan failed → full sweep
          }
        } else {
          refreshStatus();
        }

        const failed = finalResults.filter((r) => !r.success).length;
        const totalFixes = finalResults.reduce((sum, r) => sum + r.totalFixes, 0);
        if (failed > 0) {
          toast.warning(
            `Maintenance complete: ${finalResults.length - failed} succeeded, ${failed} failed, ${totalFixes} total fix(es)`,
          );
        } else if (totalFixes > 0) {
          toast.success(
            `Maintenance complete: ${finalResults.length} table(s) processed, ${totalFixes} fix(es) applied`,
          );
        } else {
          toast.info(
            `Maintenance complete: ${finalResults.length} table(s) checked — all up to date`,
          );
        }
      },
    });
  };

  const handleBulkMaintenance = () =>
    runMaintenanceFor(
      tables.filter((t) => selectedIds.has(t.id)).map((t) => t.id),
    );

  // Synchronous, bounded re-scan of specific tables — no worker sweep. Shared by
  // the bulk "Scan selected" button and the per-row "Scan" action.
  const scanTablesById = async (ids: string[]) => {
    if (!token || ids.length === 0) return;
    const res = await myappApi.tables.rescanStatus(token, ids);
    applyStatuses(res.tables); // merges status + stamps "last scanned"
  };

  const handleScanSelected = async () => {
    const ids = [...selectedIds];
    if (!token || ids.length === 0 || ids.length > SCAN_SELECTED_MAX) return;
    setIsScanningSelected(true);
    try {
      await scanTablesById(ids);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Could not scan the selected tables",
      );
    } finally {
      setIsScanningSelected(false);
    }
  };

  // Per-row "Scan": re-scan just this table (synchronous, bounded). scanningRowId
  // drives the row's spinner.
  const handleScanRow = async (table: Form) => {
    if (!token) return;
    setScanningRowId(table.id);
    try {
      await scanTablesById([table.id]);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : `Could not scan ${table.name}`,
      );
    } finally {
      setScanningRowId(null);
    }
  };

  // Per-row "Run Maintenance": repair just this table via the worker.
  const handleRunRow = (table: Form) => runMaintenanceFor([table.id]);

  const handleRecheckAll = async () => {
    if (!token) return;
    // Bust the org cache so the next poll re-scans every table, then poll.
    // NOTE: this is the heavy org-wide worker scan — it is now an explicit,
    // admin-initiated action only (never auto-run on open).
    try {
      await myappApi.tables.recheckAllStatus(token);
    } catch {
      // Even if the bust call fails, refreshStatus still re-polls.
    }
    refreshStatus();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const progressPercent =
    runProgress && runProgress.total > 0
      ? Math.round((runProgress.scanned / runProgress.total) * 100)
      : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-4">
          <Link
            to="/portal/platform/settings"
            className="flex items-center gap-2 text-sm text-primary transition hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Administration
          </Link>
        </div>
        <h1 className="mt-4 text-4xl font-bold text-slate-900">
          Table Maintenance
        </h1>
        <p className="mt-2 text-lg text-slate-600">
          Select tables and run bulk maintenance operations. Only published
          tables are shown.
        </p>
      </div>

      {/* Search + Actions */}
      <div className="flex items-center justify-between gap-4">
        {tables.length > 0 && (
          <SearchInput
            placeholder="Search tables..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm("")}
            containerClassName="max-w-sm"
          />
        )}
        <div className="flex items-center gap-2 ml-auto">
          {/* Scan ONLY the selected tables (≤5), synchronously — the safe,
              on-demand replacement for the auto-scan-on-open. */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleScanSelected}
            disabled={
              isScanningSelected ||
              scanningRowId !== null ||
              isRunning ||
              selectedIds.size === 0 ||
              selectedIds.size > SCAN_SELECTED_MAX
            }
            title={
              selectedIds.size > SCAN_SELECTED_MAX
                ? `Select up to ${SCAN_SELECTED_MAX} tables to scan at once`
                : "Scan the selected tables for maintenance issues"
            }
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${isScanningSelected ? "animate-spin" : ""}`}
            />
            {isScanningSelected
              ? "Scanning…"
              : `Scan selected (${selectedIds.size})`}
          </Button>
          {/* The heavy org-wide worker scan — now an explicit action only,
              never auto-run on open (caused the 2026-06-29 worker overload). */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRecheckAll}
            disabled={scanning || isRunning}
            title="Scan EVERY table from scratch — slow on large orgs (runs the full background scan)"
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${scanning ? "animate-spin" : ""}`}
            />
            Scan all
          </Button>
          {needingCount > 0 && (
            <>
              {/* Narrow the list to just the tables with issues. */}
              <Button
                variant={showOnlyNeedsMaintenance ? "default" : "outline"}
                size="sm"
                onClick={() => setShowOnlyNeedsMaintenance((v) => !v)}
                title="Show only tables that need maintenance"
              >
                <Filter className="h-4 w-4 mr-2" />
                Needs maintenance only
              </Button>
              {/* Tick every table with issues in one click. */}
              <Button
                variant="outline"
                size="sm"
                onClick={selectAllNeedingMaintenance}
                disabled={isRunning}
                title="Select every table that needs maintenance"
              >
                Select needing ({needingCount})
              </Button>
            </>
          )}
          <Button
            className="rounded-full"
            onClick={handleBulkMaintenance}
            disabled={selectedIds.size === 0 || isRunning}
          >
            <Wrench
              className={`h-4 w-4 mr-2 ${isRunning ? "animate-spin" : ""}`}
            />
            {isRunning
              ? runProgress
                ? `Repairing ${runProgress.scanned} of ${runProgress.total}...`
                : "Starting…"
              : `Run Maintenance (${selectedIds.size})`}
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Status-fetch failure banner. Renders when /repair/status
          errored on the last try. Without this, a failed status check
          silently leaves every row showing the default green tick,
          which misreads as "all clear" rather than "signal
          unavailable". The toast covers the moment of failure; this
          banner covers re-reads of the page during the same session. */}
      {statusFetchFailed && (
        <div
          role="status"
          className="flex items-start gap-3 rounded-lg border border-amber-500 bg-amber-50 p-3 text-amber-900"
        >
          <Wrench className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="flex-1 text-sm">
            <p className="font-medium">Maintenance status unavailable</p>
            <p className="opacity-80">
              The &ldquo;Needs Maintenance&rdquo; column below may be stale
              because the status check could not be reached or did not
              finish. The bulk Run Maintenance action is unaffected.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 border-amber-500 text-amber-900 hover:bg-amber-100"
            onClick={refreshStatus}
          >
            Retry
          </Button>
        </div>
      )}

      {/* Status-scan progress: the worker computes each table's status
          and rows fill in below as they complete. Indeterminate shimmer
          until the first table lands (no progress numbers yet). */}
      {scanning && (
        <div className="space-y-1">
          <div className="text-sm text-muted-foreground">
            Scanning tables for maintenance issues…
            {scanProgress && (
              <span className="ml-1 font-medium text-foreground">
                {scanProgress.scanned} of {scanProgress.total}
              </span>
            )}
          </div>
          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
            <div
              className={
                scanProgress
                  ? "h-full bg-primary rounded-full transition-all duration-300"
                  : "h-full w-1/4 bg-primary/60 rounded-full animate-pulse"
              }
              style={
                scanProgress
                  ? {
                      width: `${Math.round((scanProgress.scanned / Math.max(scanProgress.total, 1)) * 100)}%`,
                    }
                  : undefined
              }
            />
          </div>
        </div>
      )}

      {/* Repair progress: the worker repairs each selected table and the
          rows fill into the Results card below as they complete.
          Indeterminate shimmer until the first table lands. */}
      {isRunning && (
        <div className="space-y-1">
          <div className="text-sm text-muted-foreground">
            Repairing tables…
            {runProgress && (
              <span className="ml-1 font-medium text-foreground">
                {runProgress.scanned} of {runProgress.total}
              </span>
            )}
          </div>
          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
            <div
              className={
                runProgress
                  ? "h-full bg-primary rounded-full transition-all duration-300"
                  : "h-full w-1/4 bg-primary/60 rounded-full animate-pulse"
              }
              style={runProgress ? { width: `${progressPercent}%` } : undefined}
            />
          </div>
        </div>
      )}

      {/* Run-failure banner: the hook toasts at the moment of failure; this
          covers re-reads of the page. The worker may still be repairing
          server-side, so the wording is "uncertain", not "failed". */}
      {runFailed && !isRunning && (
        <div
          role="status"
          className="flex items-start gap-3 rounded-lg border border-amber-500 bg-amber-50 p-3 text-amber-900"
        >
          <Wrench className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="flex-1 text-sm">
            <p className="font-medium">Maintenance run status uncertain</p>
            <p className="opacity-80">
              We lost track of the run before it confirmed completion. It may
              still be finishing on the server — reload the page shortly to
              see the final result.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 border-amber-500 text-amber-900 hover:bg-amber-100"
            onClick={dismissRunFailure}
          >
            Dismiss
          </Button>
        </div>
      )}

      {/* Results summary — streams in during the run, one row per table as
          the worker completes it. */}
      {showResults && results.length > 0 && (
        <Card>
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Repair Results</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowResults(false)}
              >
                Dismiss
              </Button>
            </div>
            <div className="space-y-1">
              {results.map((r) => (
                <div key={r.tableId} className="py-1">
                <div className="flex items-center gap-2 text-sm">
                  {r.success ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 text-destructive shrink-0" />
                  )}
                  <span className="font-medium">{r.tableName}</span>
                  {r.success ? (
                    <span className="text-muted-foreground">
                      {r.totalFixes > 0
                        ? `${r.totalFixes} fix(es) applied`
                        : "up to date"}
                      {/*
                        Surface the cascade so the admin sees the BE also
                        visited the hidden Lines / Versions children. The
                        wording is honest twice over: "fixes" only when
                        child fixes were actually applied (the 2026-06-10
                        confusion was this label claiming repairs for
                        healthy children), and "includes" (not "also")
                        because child fixes are part of the headline
                        total, not in addition to it.
                      */}
                      {r.cascadedChecked > 0 && (
                        <span className="ml-1 text-xs opacity-75">
                          {r.cascadedFixed > 0
                            ? `(includes ${r.cascadedFixed} fix${r.cascadedFixed === 1 ? "" : "es"} on ${r.cascadedChecked} child table${r.cascadedChecked === 1 ? "" : "s"})`
                            : `(also checked ${r.cascadedChecked} child table${r.cascadedChecked === 1 ? "" : "s"})`}
                        </span>
                      )}
                      {r.unresolved > 0 && (
                        <span className="ml-1 text-xs text-amber-600">
                          ({r.unresolved} issue{r.unresolved === 1 ? "" : "s"}{" "}
                          detected but not fixed — see warnings)
                        </span>
                      )}
                      {r.repairErrors.length > 0 && (
                        <span className="ml-1 text-xs text-amber-600">
                          ({r.repairErrors.length} error
                          {r.repairErrors.length === 1 ? "" : "s"} during
                          repair)
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="text-destructive">{r.error}</span>
                  )}
                </div>
                {(r.repairErrors.length > 0 ||
                  (r.unresolved > 0 && r.warnings.length > 0)) && (
                  <ul className="ml-6 mt-0.5 space-y-0.5 text-xs text-muted-foreground list-disc list-inside">
                    {[...r.repairErrors, ...r.warnings]
                      .slice(0, 3)
                      .map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    {r.repairErrors.length + r.warnings.length > 3 && (
                      <li>
                        …and {r.repairErrors.length + r.warnings.length - 3}{" "}
                        more
                      </li>
                    )}
                  </ul>
                )}
                {/* Copyable manual-fix summary: rows the deterministic repair
                    jobs could not resolve (ambiguous / no match / parent-
                    unresolvable / orphan / advisory). Never machine-fixed —
                    the admin copies the table and resolves each by hand. */}
                {r.unresolvedRows.length > 0 && (
                  <div className="ml-6 mt-1 rounded border border-amber-200 bg-amber-50/60 p-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium text-amber-800">
                        {r.unresolvedTotal} link value
                        {r.unresolvedTotal === 1 ? "" : "s"} need manual review
                        {r.unresolvedTotal > r.unresolvedRows.length &&
                          ` (showing first ${r.unresolvedRows.length})`}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 shrink-0 border-amber-300 text-amber-900 hover:bg-amber-100"
                        onClick={() => {
                          void navigator.clipboard
                            .writeText(buildUnresolvedText(r))
                            .then(() =>
                              toast.success("Unresolved rows copied"),
                            )
                            .catch(() =>
                              toast.warning("Could not copy to clipboard"),
                            );
                        }}
                      >
                        <Copy className="mr-1 h-3 w-3" />
                        Copy
                      </Button>
                    </div>
                    <div className="mt-1 max-h-48 overflow-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-muted-foreground">
                            <th className="pr-2 font-medium">Grain</th>
                            <th className="pr-2 font-medium">Record</th>
                            <th className="pr-2 font-medium">Parent</th>
                            <th className="pr-2 font-medium">Field</th>
                            <th className="pr-2 font-medium">Value</th>
                            <th className="font-medium">Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.unresolvedRows.map((row, i) => (
                            <tr key={i} className="align-top">
                              <td className="pr-2">{row.grain}</td>
                              <td className="pr-2">
                                {row.record_identifier ?? "—"}
                              </td>
                              <td className="pr-2">
                                {row.parent_record ?? "—"}
                              </td>
                              <td className="pr-2">{row.field_name}</td>
                              <td className="pr-2">{row.stored_value ?? "—"}</td>
                              <td>{row.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Table list */}
      {tables.length === 0 ? (
        <Card className="p-8 text-center">
          <Wrench className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No published tables</h3>
          <p className="text-muted-foreground">
            Publish tables first to run maintenance operations on them.
          </p>
        </Card>
      ) : filteredTables.length === 0 ? (
        <Card className="p-8 text-center">
          <CheckCircle2 className="h-12 w-12 mx-auto text-green-600 mb-4" />
          {search ? (
            <>
              <h3 className="text-lg font-medium mb-2">
                No tables match your search
              </h3>
              <p className="text-muted-foreground">
                Try a different search term, or{" "}
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onClick={() => setSearchTerm("")}
                >
                  clear the search
                </button>
                .
              </p>
            </>
          ) : (
            <>
              <h3 className="text-lg font-medium mb-2">
                All tables are up to date
              </h3>
              <p className="text-muted-foreground">
                No tables currently need maintenance.{" "}
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onClick={() => setShowOnlyNeedsMaintenance(false)}
                >
                  Show all tables
                </button>
              </p>
            </>
          )}
        </Card>
      ) : (
        <Card>
          {/* Proxy top scrollbar — mirrors the table's horizontal scroll so a
              wide table can be dragged from the top too and never bleeds off the
              page on small screens. Hides itself when the table fits. */}
          <SyncedTopScrollbar
            targetRef={tableScrollRef}
            contentWidth={tableContentWidth}
            className="rounded-t-xl border-b"
          />
          {/* Bounded scroll container: horizontal (bottom bar) + vertical, so the
              header, the first column and the right-hand action buttons can all
              stay pinned (sticky) while the rest scrolls. */}
          <div ref={tableScrollRef} className="overflow-auto max-h-[70vh]">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead
                    direction={null}
                    onSort={() => {}}
                    disabled
                    className="w-12 sticky top-0 left-0 z-30 bg-background"
                  >
                    <Checkbox
                      checked={
                        filteredTables.length > 0 &&
                        filteredTables.every((t) => selectedIds.has(t.id))
                      }
                      onCheckedChange={toggleSelectAll}
                      disabled={isRunning}
                    />
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("name")}
                    onSort={() => requestSort("name")}
                    className={cn(
                      "sticky top-0 left-12 z-30 bg-background",
                      STICKY_LEFT_SHADOW,
                    )}
                  >
                    Table Name
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("table_name")}
                    onSort={() => requestSort("table_name")}
                    className="sticky top-0 z-20 bg-background"
                  >
                    Physical Table
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("table_type")}
                    onSort={() => requestSort("table_type")}
                    className="sticky top-0 z-20 bg-background"
                  >
                    Type
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("status_rank")}
                    onSort={() => requestSort("status_rank")}
                    className="sticky top-0 z-20 bg-background"
                  >
                    Status
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("last_published_at_utc")}
                    onSort={() => requestSort("last_published_at_utc")}
                    className="sticky top-0 z-20 bg-background"
                  >
                    Last Published
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("last_scanned_at")}
                    onSort={() => requestSort("last_scanned_at")}
                    className="sticky top-0 z-20 bg-background"
                  >
                    Last Scanned
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("last_repair_run_at_utc")}
                    onSort={() => requestSort("last_repair_run_at_utc")}
                    className="sticky top-0 z-20 bg-background"
                  >
                    Last Repaired
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("link_fk_upgraded_at_utc")}
                    onSort={() => requestSort("link_fk_upgraded_at_utc")}
                    className="sticky top-0 z-20 bg-background"
                  >
                    Per-field FK
                  </SortableTableHead>
                  <SortableTableHead
                    direction={null}
                    onSort={() => {}}
                    disabled
                    className={cn(
                      "sticky top-0 right-0 z-30 bg-background text-right",
                      STICKY_RIGHT_SHADOW,
                    )}
                  >
                    Actions
                  </SortableTableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedTables.map((table) => {
                  // Reuse the UTC-anchored Date parsed once in tablesWithScan
                  // (the same value the sort key uses) — no second parse here.
                  const lastRepairedAt = table.last_repaired_at;
                  const hasRepairStamp = Boolean(table.last_repair_run_at_utc);
                  return (
                  <TableRow key={table.id}>
                    <TableCell className="w-12 sticky left-0 z-10 bg-background">
                      <Checkbox
                        checked={selectedIds.has(table.id)}
                        onCheckedChange={() => toggleSelect(table.id)}
                        disabled={isRunning}
                      />
                    </TableCell>
                    <TableCell
                      className={cn(
                        "font-medium sticky left-12 z-10 bg-background",
                        STICKY_LEFT_SHADOW,
                      )}
                    >
                      {table.name}
                    </TableCell>
                    <TableCell className="text-muted-foreground font-mono text-xs">
                      {table.table_name}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{table.table_type}</Badge>
                    </TableCell>
                    <TableCell>
                      <NeedsMaintenanceCell
                        status={statusByTableId[table.id]}
                        // Skeleton while a scan is in flight for this row: the
                        // org sweep (scanning), a "Scan selected" covering this
                        // ticked row, or this row's own per-row scan.
                        isLoading={
                          statusLoading ||
                          (scanning && !statusByTableId[table.id]) ||
                          (isScanningSelected && selectedIds.has(table.id)) ||
                          scanningRowId === table.id
                        }
                        // No scan this session → show "Not scanned", not a green
                        // tick. needs_republish still surfaces (row-derived).
                        notScanned={!statusByTableId[table.id]}
                        needsRepublish={table.needs_republish ?? false}
                      />
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs whitespace-nowrap">
                      {table.last_published_at_utc
                        ? new Date(table.last_published_at_utc).toLocaleString()
                        : "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs whitespace-nowrap">
                      {lastScannedByTableId[table.id] ? (
                        new Date(lastScannedByTableId[table.id]).toLocaleString()
                      ) : (
                        <span className="italic opacity-70">Not scanned</span>
                      )}
                    </TableCell>
                    <TableCell
                      className="text-muted-foreground text-xs whitespace-nowrap"
                      title={
                        lastRepairedAt
                          ? lastRepairedAt.toISOString()
                          : hasRepairStamp
                            ? "Repair timestamp is unreadable"
                            : "No repair has ever been run on this table"
                      }
                    >
                      {lastRepairedAt ? (
                        lastRepairedAt.toLocaleString()
                      ) : (
                        <span className="italic opacity-70">Never</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {/* The Upgrade button lives HERE (Per-field FK column) so it
                          doubles as the at-a-glance "which tables still need the
                          SysFk upgrade" indicator: green date = done, Upgrade
                          button = needed, ? = not scanned, — = nothing to do. */}
                      {table.link_fk_upgraded_at_utc ? (
                        <span className="inline-flex items-center gap-1 text-xs text-green-700 whitespace-nowrap">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          {new Date(
                            table.link_fk_upgraded_at_utc,
                          ).toLocaleDateString()}
                        </span>
                      ) : statusLoading ||
                        (scanning && !statusByTableId[table.id]) ||
                        (isScanningSelected && selectedIds.has(table.id)) ||
                        scanningRowId === table.id ? (
                        <Spinner className="h-3.5 w-3.5" />
                      ) : !statusByTableId[table.id] ? (
                        // Not scanned / status unknown — never a definitive "—".
                        <span
                          className="text-muted-foreground text-xs"
                          title="Not scanned — scan this table to check whether an upgrade is needed"
                        >
                          ?
                        </span>
                      ) : needsLinkFkUpgrade(table.id) ? (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={isRunning || upgradingId === table.id}
                          onClick={() => setUpgradeTarget(table)}
                        >
                          {upgradingId === table.id ? (
                            <Spinner className="h-3.5 w-3.5" />
                          ) : (
                            <Wrench className="h-3.5 w-3.5 mr-1" />
                          )}
                          {upgradingId === table.id ? "Upgrading…" : "Upgrade"}
                        </Button>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell
                      className={cn(
                        "sticky right-0 z-10 bg-background",
                        STICKY_RIGHT_SHADOW,
                      )}
                    >
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          asChild
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          title={`View ${table.name}'s structure in the table designer (new tab)`}
                        >
                          <Link
                            to="/portal/myapp/$tableId/edit"
                            params={{ tableId: table.id }}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Eye className="h-3.5 w-3.5" />
                            <span className="ml-1 hidden lg:inline">Structure</span>
                          </Link>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2"
                          // One scan at a time (like Run): a scan in flight greys
                          // every Scan button so a second click can't cut the
                          // first short. Active row shows the spinner.
                          disabled={
                            scanningRowId !== null ||
                            isScanningSelected ||
                            isRunning
                          }
                          onClick={() => handleScanRow(table)}
                          title={`Scan ${table.name} for maintenance issues`}
                        >
                          {scanningRowId === table.id ? (
                            <Spinner className="h-3.5 w-3.5" />
                          ) : (
                            <RefreshCw className="h-3.5 w-3.5" />
                          )}
                          <span className="ml-1 hidden sm:inline">Scan</span>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2"
                          // One action at a time: also greyed while any scan is
                          // in flight, mirroring how Scan greys during a Run.
                          disabled={
                            isRunning ||
                            scanningRowId !== null ||
                            isScanningSelected
                          }
                          onClick={() => handleRunRow(table)}
                          title={`Run maintenance on ${table.name}`}
                        >
                          <Wrench className="h-3.5 w-3.5" />
                          <span className="ml-1 hidden sm:inline">Run</span>
                        </Button>
                        {/* 4th pinned action: Upgrade (SysFk). Always present so
                            the action row is consistent; enabled + highlighted
                            only when a scanned table actually needs the per-field
                            FK upgrade. (The Per-field FK column still shows the
                            ?/—/Upgrade indicator too.) */}
                        <Button
                          variant={
                            needsLinkFkUpgrade(table.id) ? "default" : "outline"
                          }
                          size="sm"
                          className="h-7 px-2"
                          disabled={
                            !needsLinkFkUpgrade(table.id) ||
                            isRunning ||
                            upgradingId === table.id
                          }
                          onClick={() => setUpgradeTarget(table)}
                          title={
                            table.link_fk_upgraded_at_utc
                              ? `Per-field FK already upgraded (${new Date(
                                  table.link_fk_upgraded_at_utc,
                                ).toLocaleDateString()})`
                              : !statusByTableId[table.id]
                                ? "Scan this table first to check whether a SysFk upgrade is needed"
                                : needsLinkFkUpgrade(table.id)
                                  ? "Upgrade link table fields (adds per-field SysFk)"
                                  : "No per-field FK upgrade needed"
                          }
                        >
                          {upgradingId === table.id ? (
                            <Spinner className="h-3.5 w-3.5" />
                          ) : (
                            <Wrench className="h-3.5 w-3.5" />
                          )}
                          <span className="ml-1 hidden sm:inline">Upgrade</span>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          {totalItems > 0 && (
            <div className="p-4">
              <Pagination
                page={currentPage}
                totalPages={totalPages}
                totalCount={totalItems}
                pageSize={itemsPerPage}
                pageSizeOptions={[10, 25, 50, 100]}
                startIndex={startIndex}
                endIndex={endIndex}
                canGoPrev={currentPage > 1}
                canGoNext={currentPage < totalPages}
                onPageChange={setCurrentPage}
                onPageSizeChange={setItemsPerPage}
                onFirstPage={() => setCurrentPage(1)}
                onLastPage={() => setCurrentPage(totalPages)}
                onPrevPage={() => setCurrentPage(currentPage - 1)}
                onNextPage={() => setCurrentPage(currentPage + 1)}
              />
            </div>
          )}
        </Card>
      )}

      <AlertDialog
        open={upgradeTarget !== null}
        onOpenChange={(open) => !open && setUpgradeTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Upgrade link table fields?</AlertDialogTitle>
            <AlertDialogDescription>
              This adds a dedicated foreign-key column (SysFk…) for each link
              field on <strong>{upgradeTarget?.name}</strong> and its line/version
              tables, then reconstructs each link's FK for existing rows from its
              stored display value — only where that value matches a single linked
              record. Ambiguous values are left unlinked; the snapshot is still
              shown. Your records' displayed values are never changed, and it's
              safe to re-run.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleConfirmUpgrade();
              }}
            >
              Upgrade
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
